<template>
  <div>
    <Card :class="$style.automationContainer">
      <ToggleSwitch
        :key="`${$options.settings.toggleAutomation.key}-${item.membershipAutomated}`"
        :data-test-id="$testId('automationToggle')"
        :label="$options.settings.toggleAutomation.label"
        :labelOn="$options.settings.toggleAutomation.labelOn"
        :value="item.membershipAutomated"
        @input="handleAutomationSuggestionChange"
      />
    </Card>
    <Card :class="$style.automationContainer">
      <ToggleSwitch
        :key="`${$options.settings.toggleEmails.key}-${item.memberSuggestionsNotify}`"
        :data-test-id="$testId('emailToggle')"
        :isDisabled="isDisabled"
        :label="$options.settings.toggleEmails.label"
        :labelOn="$options.settings.toggleEmails.labelOn"
        :value="item.memberSuggestionsNotify"
        @input="handleEmailSuggestionNotificationChange"
      />
      <TooltipIcon
        :class="$style.tooltipContainer"
        :icon="$options.settings.tooltip.icon"
        :text="$options.settings.tooltip.text"
      />
    </Card>
    <ModalConfirmation
      :class="$style.modal"
      hasBlur
      :primaryButtonProps="$options.settings.modalConfirmation.primaryButtonProps"
      :showModal="showModal"
      :title="$options.settings.modalConfirmation.title"
      @cancel="handleCancel"
      @save="handleContinue"
    >
      <template v-slot:body>
        <ParagraphBody>
          {{ $options.settings.modalConfirmation.bodyContent }}
        </ParagraphBody>
        <ParagraphBody>
          {{ $options.settings.modalConfirmation.bodySubContent }}
        </ParagraphBody>
      </template>
      <template v-slot:footerAddition>
        <Link
          :href="$options.settings.link.kbLink"
          :isExternal="true"
          target="_blank"
          :text="$options.settings.link.text"
        />
      </template>
    </ModalConfirmation>
  </div>
</template>

<script>
import {
  Card,
  Link,
  ModalConfirmation,
  ParagraphBody,
  ToggleSwitch,
  TooltipIcon,
  Types,
} from '@jumpcloud/ui-components';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

const { PrimaryButtonProps } = Types;

const settings = {
  link: {
    // TODO: use a related kblink later.
    kbLink: KbArticleUrls.attributeDrivenGroupCreation,
    text: 'Learn More About Automation',
  },
  modalConfirmation: {
    bodyContent: 'This group’s membership suggestions will be converted from manual to automated. Membership will be updated automatically based on the attribute value conditions that have been set for this group.',
    bodySubContent: 'Are you sure you want to continue?',
    primaryButtonProps: PrimaryButtonProps({ text: 'Continue' }),
    title: 'Automation Settings',
  },
  toggleAutomation: {
    key: 'ams',
    label: 'Automate Membership Suggestions Off',
    labelOn: 'Automate Membership Suggestions On',
  },
  toggleEmails: {
    key: 'mse',
    label: 'Membership Suggestion Emails Off',
    labelOn: 'Membership Suggestion Emails On',
  },
  tooltip: {
    icon: 'info',
    text: 'Email notifications for group membership suggestions will only be sent for manually managed groups.',
  },
};

export default {
  name: 'MembershipSuggestionSettings',

  components: {
    Card,
    Link,
    ModalConfirmation,
    ParagraphBody,
    ToggleSwitch,
    TooltipIcon,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  settings,

  data() {
    return {
      showModal: false,
      automationEnabled: false,
    };
  },

  computed: {
    isDisabled() {
      return (this.item.membershipAutomated === true);
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    if (this.isDisabled) this.$emit('isAutomationEnabled', true);
  },

  methods: {
    handleAutomationSuggestionChange() {
      const isGroupMembershipAutomated = !!this.item.membershipAutomated;

      const shouldTriggerAutomationConfirmationModal = !isGroupMembershipAutomated;

      if (shouldTriggerAutomationConfirmationModal) this.showModal = true;

      this.setItemFieldValue({ key: 'membershipAutomated', value: shouldTriggerAutomationConfirmationModal });

      this.$emit('isAutomationEnabled', shouldTriggerAutomationConfirmationModal);
    },

    handleCancel() {
      this.showModal = false;
      const value = false;

      this.setItemFieldValue({ key: 'membershipAutomated', value });
      this.$emit('isAutomationEnabled', false);
    },

    handleContinue() {
      this.showModal = false;
      this.setItemFieldValue({ key: 'membershipAutomated', value: true });
      this.setItemFieldValue({ key: 'memberSuggestionsNotify', value: false });

      this.$emit('isAutomationEnabled', true);
    },

    handleEmailSuggestionNotificationChange() {
      const value = !this.item.memberSuggestionsNotify;
      this.setItemFieldValue({ key: 'memberSuggestionsNotify', value });
    },

    setItemFieldValue({ key, value }) {
      this.setItemField({
        key,
        value,
      });
    },
  },
};
</script>
<style module scoped>

.automationContainer {
  align-items: center;
  display: flex;
  flex-direction: row;
  margin-top: 1rem;
  max-height: 4.375rem;
}

label[data-test-id=ToggleSwitch__automation] span:nth-child(3),
label[data-test-id=ToggleSwitch__emails] span:nth-child(3) {
  font-weight: 700;
}

.modal.modal {
  --modal-width: 64ch;
  --modal-max-width: 64ch;
}

.tooltipContainer {
  margin: 0 0 0.25rem 0.625rem;
}
</style>
